import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { toast as sonnerToast } from './ui/sonner';
import { useNavigate } from 'react-router-dom';
import { flag } from '../config';
import { api, errorMessage } from '../lib/api';
import { trackLogin } from '../utils/analytics';
import SiteAuthModal from './site/auth/SiteAuthModal';
import { safeRedirectPath } from './site/auth/authLinks';

const VERIFICATION_SENT_TEXT = 'Check your email to set your password';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

/**
 * The only <Toaster /> mounted in App.js is sonner's, so the shadcn `useToast`
 * queue this component used to write to rendered nowhere — every toast below,
 * including the Phase 1 copy fixes, was invisible. Same call shape, real output.
 */
const toast = ({ title, description, variant } = {}) => {
  const body = description ? { description } : undefined;
  if (variant === 'destructive') return sonnerToast.error(title, body);
  return sonnerToast.success(title, body);
};

// V5 Design Language
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    borderLight: 'rgba(30, 27, 75, 0.1)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

// Load Google Identity Services script once at module level
// This ensures the script starts loading immediately, not when the modal opens
if (typeof window !== 'undefined' && !document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * The customer sign-in / sign-up modal (App.js AuthModalManager: every page except /admin*),
 * opened by AuthContext.openAuthModal(mode). SLOT_MODEL on: the redesigned dialog
 * (components/site/auth: SiteAuthModal, loaded on demand). SLOT_MODEL off: the legacy V5
 * modal below, markup unchanged (phase6.chrome.qa compares it with f6f407fd).
 * Both follow a post-auth redirect only when it is a same-origin path (safeRedirectPath).
 */
const AuthModal = (props) => (flag('SLOT_MODEL') ? <SiteAuthModal {...props} /> : <LegacyAuthModal {...props} />);

const LegacyAuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    referral_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Slot model (Phase 6c): signup on an email that holds codes from an email-only claim
  // answers 202 and emails a set-password link; this holds the message to show instead.
  const [verificationSent, setVerificationSent] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState(false);
  const googleButtonRef = useRef(null);
  const googleCallbackRef = useRef(null);
  const panelRef = useRef(null);
  // The 202 "check your email" panel takes focus when it replaces the form (the focused
  // submit button unmounts with the form), so screen readers read it and focus is not lost.
  const verificationRef = useRef(null);
  const { signup, login, postAuthRedirect, clearPostAuthRedirect, pendingClaim, completePendingClaim } = useAuth();
  const navigate = useNavigate();

  // Detect preview claim context for context-aware messaging
  const isPreviewClaimContext = !!sessionStorage.getItem('preview_hub_id') || !!pendingClaim;
  const previewHubName = sessionStorage.getItem('preview_hub_name') || 'this venue';
  const pendingDealTitle = pendingClaim?.deal_title;

  // Reset form and google state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setVerificationSent('');
      setGoogleReady(false); // Reset since the overlay div is recreated
    }
  }, [isOpen]);

  // The set-password notice belongs to the signup attempt; switching mode drops it.
  useEffect(() => {
    setVerificationSent('');
  }, [mode]);

  useEffect(() => {
    if (verificationSent && verificationRef.current) verificationRef.current.focus();
  }, [verificationSent]);

  // Sync mode with initialMode prop only when modal opens or initialMode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Initialize Google Identity Services and render button into overlay when modal opens
  useEffect(() => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) return;
    if (!isOpen) return; // Only render when modal is open (ref exists)

    const renderGoogleButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: (response) => {
          // Use ref to always call the latest version of the callback
          googleCallbackRef.current?.(response);
        },
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: 400,
      });

      setGoogleReady(true);
    };

    // If Google SDK is already loaded, render with a short delay for ref attachment
    if (window.google?.accounts?.id) {
      const timer = setTimeout(renderGoogleButton, 50);
      return () => clearTimeout(timer);
    }

    // Poll until the SDK loads (script is already loading from module-level)
    const checkInterval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(checkInterval);
        renderGoogleButton();
      }
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.google?.accounts?.id) {
        console.error('Google Identity Services failed to load');
        setGoogleError(true);
      }
    }, 10000);
    return () => { clearInterval(checkInterval); clearTimeout(timeout); };
  }, [isOpen]);

  // Escape closes the modal and Tab stays inside it while it is open.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (panel) (panel.querySelector(FOCUSABLE) || panel).focus();
    }, 50);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [isOpen, onClose]);

  // Google callback handler — processes the ID token from Google
  const handleGoogleCallback = async (response) => {
    setLoading(true);
    try {
      // Send Google ID token to backend
      const { data } = await api.post('/auth/google', { credential: response.credential });

      // Store token and update auth context
      localStorage.setItem('token', data.token);
      trackLogin({ method: 'google' });

      if (data.account_linked) {
        toast({
          title: 'Google account linked!',
          description: 'Your Google account has been linked to your Tappd account.'
        });
      } else {
        toast({
          title: 'Welcome!',
          description: data.user.first_name ? `Welcome, ${data.user.first_name}!` : 'Successfully signed in with Google.'
        });
      }

      // Close modal and redirect
      onClose();

      // Check for pending redirect (persisted in sessionStorage, survives reload). Only a
      // same-origin path is followed: `?redirect=` on /signup lands here, and an absolute
      // URL would otherwise send the fresh session to another site (open redirect).
      const redirectTo = safeRedirectPath(sessionStorage.getItem('postAuthRedirect'));
      sessionStorage.removeItem('postAuthRedirect');
      if (redirectTo) {
        window.location.href = redirectTo;
      } else {
        window.location.reload();
      }
    } catch (error) {
      toast({
        title: 'Login failed',
        description: errorMessage(error, 'Failed to sign in with Google'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Keep callback ref always pointing to the latest version (avoids stale closure in useEffect)
  googleCallbackRef.current = handleGoogleCallback;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); // Clear any previous errors

    if (mode === 'signup') {
      const result = await signup(formData);
      if (result.verificationSent) {
        // Not an error: the address already holds codes; it gets a set-password link.
        setVerificationSent(result.message || VERIFICATION_SENT_TEXT);
        setLoading(false);
        return;
      }
      if (result.success) {
        // Check if user was linked to a business via invite
        if (result.business_linked) {
          toast({
            title: '🎉 Welcome to Tappd!',
            description: 'Account created! You\'ve been linked to your business.'
          });
          onClose();

          // Clear any stored business invite data
          sessionStorage.removeItem('pending_business_invite_token');
          sessionStorage.removeItem('pending_business_invite_business_id');

          // Redirect to business dashboard
          navigate('/business/dashboard');
        } else {
          // Calculate total bonus (welcome + referral)
          const totalBonus = (result.welcome_bonus || 0) + (result.referral_bonus || 0);
          let bonusMessage = 'Welcome to Tappd!';

          if (flag('REWARDS') && totalBonus > 0) {
            bonusMessage = result.referral_bonus > 0
              ? `You earned ${totalBonus} Taps (${result.welcome_bonus} welcome + ${result.referral_bonus} referral)!`
              : `You earned ${result.welcome_bonus} welcome Taps!`;
          }

          onClose();

          // Check for pending claim from preview mode
          if (pendingClaim || sessionStorage.getItem('preview_hub_id')) {
            const claimResult = await completePendingClaim();
            if (claimResult.success && !claimResult.unlockOnly) {
              // Deal was claimed - show combined message
              toast({
                title: '🎉 Deal Claimed!',
                description: flag('REWARDS')
                  ? `+${(result.welcome_bonus || 0) + 25} Taps earned!`
                  : 'Your code is saved in your Deal Wallet.'
              });
            } else {
              // Just unlocked hub, no claim
              toast({
                title: '🎉 Welcome to Tappd!',
                description: bonusMessage
              });
            }
            const redirectTo = safeRedirectPath(postAuthRedirect) || '/local-deals-near-me';
            clearPostAuthRedirect();
            navigate(redirectTo);
          } else {
            toast({
              title: '🎉 Welcome to Tappd!',
              description: bonusMessage
            });
            const redirectTo = safeRedirectPath(postAuthRedirect) || '/profile';
            clearPostAuthRedirect();
            navigate(redirectTo);
          }
        }
      } else {
        setError(result.error);
        toast({ title: 'Signup failed', description: result.error, variant: 'destructive' });
      }
    } else {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        onClose();

        // Check for pending claim from preview mode
        if (pendingClaim || sessionStorage.getItem('preview_hub_id')) {
          const claimResult = await completePendingClaim();
          if (claimResult.success && !claimResult.unlockOnly) {
            toast({
              title: '🎉 Deal Claimed!',
              description: flag('REWARDS') ? '+25 Taps earned!' : 'Your code is saved in your Deal Wallet.'
            });
          } else {
            toast({ title: 'Welcome back!', description: 'You are now logged in.' });
          }
          const redirectTo = safeRedirectPath(postAuthRedirect) || '/local-deals-near-me';
          clearPostAuthRedirect();
          navigate(redirectTo);
        } else {
          toast({ title: 'Welcome back!', description: 'You are now logged in.' });
          if (postAuthRedirect) {
            const redirectTo = safeRedirectPath(postAuthRedirect);
            clearPostAuthRedirect();
            if (redirectTo) navigate(redirectTo);
          }
        }
      } else {
        setError(result.error);
        toast({ title: 'Login failed', description: result.error, variant: 'destructive' });
      }
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - V5 Navy overlay */}
      <div
        className="fixed inset-0 z-[100] backdrop-blur-sm"
        style={{
          backgroundColor: 'rgba(30, 27, 75, 0.85)',
          animation: 'fadeIn 0.2s ease-out'
        }}
      />

      {/* Modal Container - Centered with proper overflow */}
      <div
        className="fixed inset-0 z-[101] overflow-y-auto p-4"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="min-h-full flex items-center justify-center">
          <div
            className="relative w-full max-w-md my-8 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={mode === 'signup' ? 'Create your Tappd account' : 'Sign in to Tappd'}
            tabIndex={-1}
            style={{
              animation: 'scaleIn 0.2s ease-out',
              borderRadius: '24px',
              boxShadow: '0 25px 50px -12px rgba(30, 27, 75, 0.4)'
            }}
          >
            {/* V5 Navy Header */}
            <div
              className="px-8 pt-8 pb-6 relative overflow-hidden"
              style={{ backgroundColor: v5.colors.navy }}
            >
              {/* Decorative grid pattern */}
              <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
                  `,
                  backgroundSize: '40px 40px',
                }}
              />

              {/* Gradient accents */}
              <div
                className="absolute top-0 right-0 w-32 h-32"
                style={{
                  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
                }}
              />
              <div
                className="absolute bottom-0 left-0 w-24 h-24"
                style={{
                  background: 'radial-gradient(circle, rgba(249, 115, 22, 0.2) 0%, transparent 70%)',
                }}
              />

              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full transition-colors z-10"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                aria-label="Close"
              >
                <X size={20} style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
              </button>

              {/* Header Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: 'rgba(139, 92, 246, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontFamily: v5.fonts.mono
                    }}
                  >
                    {mode === 'login' ? '// welcome_back' : '// join_tappd'}
                  </span>
                </div>
                <h2
                  className="text-2xl font-medium text-white"
                  style={{ fontFamily: v5.fonts.display }}
                >
                  {isPreviewClaimContext
                    ? (mode === 'signup' ? 'Save Your Spot' : 'Welcome Back')
                    : (mode === 'login' ? 'Welcome Back' : 'Join Tappd')}
                </h2>
                <p
                  className="text-sm mt-1"
                  style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: v5.fonts.body }}
                >
                  {isPreviewClaimContext
                    ? `Create a free account to claim${pendingDealTitle ? ` "${pendingDealTitle}"` : ' deals'} and keep ${previewHubName} unlocked.`
                    : flag('SLOT_MODEL')
                      // Slot model: claiming needs only an email; an account keeps the codes together.
                      ? (mode === 'login' ? 'Sign in to see your codes' : 'Create an account to keep your codes in one place')
                      : (mode === 'login'
                        ? 'Sign in to see your claimed deals'
                        : 'Create a free account to claim deals')}
                </p>
              </div>

              {/* V5 Purple-Orange Accent Line */}
              <div
                className="absolute bottom-0 left-0 w-full h-1"
                style={{ background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)` }}
              />
            </div>

            {/* Content - Cream background */}
            <div
              className="p-8"
              style={{ backgroundColor: v5.colors.cream }}
            >
              {/* Error Message Display - V5 Style */}
              {error && (
                <div
                  className="mb-6 p-4 rounded-xl flex items-start gap-3"
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)'
                  }}
                >
                  <AlertCircle
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: '#DC2626' }}
                  />
                  <p
                    className="text-sm font-medium"
                    style={{ color: '#DC2626', fontFamily: v5.fonts.body }}
                  >
                    {error}
                  </p>
                </div>
              )}

              {verificationSent && (
                <div
                  ref={verificationRef}
                  tabIndex={-1}
                  role="status"
                  aria-live="polite"
                  data-testid="signup-verification-sent"
                  className="mb-6 p-4 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                  style={{
                    backgroundColor: v5.colors.white,
                    border: `1px solid ${v5.colors.borderLight}`,
                    fontFamily: v5.fonts.body,
                    color: v5.colors.textPrimary
                  }}
                >
                  <p className="text-base font-semibold">{verificationSent}</p>
                  <p className="mt-1 text-sm" style={{ color: v5.colors.textSecondary }}>
                    We sent a link to <strong>{formData.email}</strong>. Open it to set your password. Your codes
                    stay in your wallet.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setVerificationSent(''); setMode('login'); }}
                    className="mt-3 min-h-[44px] text-sm font-semibold hover:underline"
                    style={{ color: v5.colors.purpleDark }}
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              {!verificationSent && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="auth-email"
                    className="block text-sm font-medium mb-2"
                    style={{ color: v5.colors.textSecondary, fontFamily: v5.fonts.body }}
                  >
                    Email
                  </label>
                  <Input
                    id="auth-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="rounded-xl h-11 md:h-9 border-2 focus:ring-2 focus:ring-purple-500/20"
                    style={{
                      borderColor: v5.colors.borderLight,
                      fontFamily: v5.fonts.body,
                      backgroundColor: v5.colors.white
                    }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="auth-password"
                    className="block text-sm font-medium mb-2"
                    style={{ color: v5.colors.textSecondary, fontFamily: v5.fonts.body }}
                  >
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="auth-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="rounded-xl h-11 md:h-9 pr-10 border-2 focus:ring-2 focus:ring-purple-500/20"
                      style={{
                        borderColor: v5.colors.borderLight,
                        fontFamily: v5.fonts.body,
                        backgroundColor: v5.colors.white
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: v5.colors.textMuted }}
                    >
                      {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                {mode === 'login' && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate('/forgot-password');
                      }}
                      className="text-sm font-medium hover:underline transition-colors"
                      style={{ color: v5.colors.purple, fontFamily: v5.fonts.body }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {/* Referral Code (Signup only; referrals are a REWARDS feature) */}
                {mode === 'signup' && flag('REWARDS') && (
                  <div className="pt-1">
                    {!showReferral ? (
                      <button
                        type="button"
                        onClick={() => setShowReferral(true)}
                        className="text-sm font-medium hover:underline flex items-center gap-2 transition-colors"
                        style={{ color: v5.colors.purple, fontFamily: v5.fonts.body }}
                      >
                        <Sparkles size={16} />
                        Have a referral code?
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <label
                          htmlFor="auth-referral-code"
                          className="text-sm font-medium"
                          style={{ color: v5.colors.textSecondary, fontFamily: v5.fonts.body }}
                        >
                          Referral Code (optional)
                        </label>
                        <Input
                          id="auth-referral-code"
                          type="text"
                          placeholder="XXXXXXXX"
                          value={formData.referral_code}
                          onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                          className="rounded-xl uppercase border-2"
                          style={{
                            borderColor: v5.colors.borderLight,
                            fontFamily: v5.fonts.mono,
                            backgroundColor: v5.colors.white
                          }}
                          maxLength={8}
                        />
                        <p
                          className="text-xs"
                          style={{ color: v5.colors.textMuted, fontFamily: v5.fonts.body }}
                        >
                          You&apos;ll get 50 bonus Taps when you sign up with a referral code!
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* V5 Purple Primary Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl py-3 min-h-[44px] text-base font-semibold transition-all hover:-translate-y-0.5"
                  style={{
                    backgroundColor: v5.colors.purple,
                    color: '#FFFFFF',
                    fontFamily: v5.fonts.body,
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                </Button>
              </form>
              )}

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: v5.colors.borderLight }}></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span
                    className="px-4"
                    style={{
                      backgroundColor: v5.colors.cream,
                      fontFamily: v5.fonts.body,
                      color: v5.colors.textMuted
                    }}
                  >
                    or continue with
                  </span>
                </div>
              </div>

              {/* Google Login Button — transparent overlay technique */}
              <div style={{ position: 'relative' }}>
                {/* Visual button (what the user sees) */}
                <Button
                  type="button"
                  disabled={loading || googleError}
                  className="w-full rounded-xl py-3 text-base font-semibold flex items-center justify-center gap-3 border-2 transition-all hover:-translate-y-0.5"
                  style={{
                    backgroundColor: v5.colors.white,
                    color: googleError ? v5.colors.textMuted : v5.colors.textPrimary,
                    borderColor: v5.colors.borderLight,
                    fontFamily: v5.fonts.body,
                  }}
                  tabIndex={-1}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.509h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4"/>
                    <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853"/>
                    <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.341-2.59z" fill="#FBBC05"/>
                    <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.341 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335"/>
                  </svg>
                  {googleError ? 'Google unavailable' : 'Google'}
                </Button>

                {/* Invisible Google-rendered button overlay (receives the real user click) */}
                {!googleError && (
                  <div
                    ref={googleButtonRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0.001, // Nearly invisible but still interactive
                      zIndex: 1,
                      overflow: 'hidden',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      // DO NOT set pointer-events: none — this must receive real clicks
                    }}
                    aria-label="Sign in with Google"
                  />
                )}
              </div>

              {/* Toggle Login/Signup */}
              <div className="mt-6 text-center">
                <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textSecondary, fontSize: '14px' }}>
                  {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                  <button
                    type="button"
                    onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                    className="font-semibold hover:underline transition-colors"
                    style={{ color: v5.colors.purple }}
                  >
                    {mode === 'login' ? 'Sign up' : 'Sign in'}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default AuthModal;
